/**
 * Normalize API plan shapes (legacy steps / nodeStrategy / plannedNodes) for compact cards and modal display.
 */

export function getPlanEdgeCount(plan, preview) {
  if (preview?.graph?.edges?.length) return preview.graph.edges.length;
  if (plan?.edges?.length) return plan.edges.length;
  return 0;
}

/** Canonical edge list for UI: preview.graph.edges, then plan.edges */
export function getPreviewEdges(preview, plan) {
  const raw = preview?.graph?.edges?.length
    ? preview.graph.edges
    : plan?.edges || [];
  return raw.map((e) => ({
    fromStep: e.fromStep,
    toStep: e.toStep,
    label: e.label != null ? String(e.label) : e.kind != null ? String(e.kind) : "",
    kind: e.kind,
    fallback:
      !!e.fallback ||
      String(e.label || "").toLowerCase() === "default" ||
      e.kind === "fallback",
  }));
}

export function normalizePlanForDisplay(plan) {
  if (!plan) {
    return { steps: [], variables: [], connections: [], branchingRows: [] };
  }

  let steps = [];
  if (Array.isArray(plan.plannedNodes) && plan.plannedNodes.length) {
    steps = plan.plannedNodes.map((n) => ({
      step: n.step,
      nodeType: n.nodeType,
      title: n.title,
      description: [n.title, n.userSummary, n.builderNotes].filter(Boolean).join(" — "),
    }));
  } else if (Array.isArray(plan.steps) && plan.steps.length) {
    steps = plan.steps;
  } else if (Array.isArray(plan.nodeStrategy) && plan.nodeStrategy.length) {
    steps = plan.nodeStrategy.map((s) => ({
      step: s.step,
      nodeType: s.nodeType,
      description: [s.title, s.purpose, s.outputBehavior].filter(Boolean).join(" — "),
    }));
  }

  let variables = [];
  if (Array.isArray(plan.variables) && plan.variables.length) {
    variables = plan.variables;
  } else if (Array.isArray(plan.variablePlan) && plan.variablePlan.length) {
    variables = plan.variablePlan.map((v) => ({
      name: v.name,
      producedBy:
        v.createdByStep != null ? `step ${v.createdByStep}` : "",
      description:
        v.description ||
        (Array.isArray(v.usedInSteps) && v.usedInSteps.length
          ? `used in steps ${v.usedInSteps.join(", ")}`
          : ""),
    }));
  }

  const connections = Array.isArray(plan.connections) ? plan.connections : [];

  let branchingRows = [];
  if (Array.isArray(plan.branchingStrategy) && plan.branchingStrategy.length) {
    branchingRows = plan.branchingStrategy.map((b) => ({
      fromStep: b.sourceStep,
      label: b.rule || b.sourceNodeType || "",
      toStep: b.goesToStep,
      fallback: !!b.fallback,
    }));
  }

  return { steps, variables, connections, branchingRows };
}

/**
 * Wiring rows for modal (step → label → step).
 * Set legacyOnly: true to skip plan.edges / preview.graph (e.g. when comparing).
 */
export function getPreviewBranches(preview, plan, options = {}) {
  if (!options.legacyOnly) {
    const interEdges = getPreviewEdges(preview, plan);
    if (interEdges.length > 0) {
      return interEdges.map((e) => ({
        fromStep: e.fromStep,
        label: e.label || e.kind || "→",
        toStep: e.toStep,
        fallback: e.fallback,
        kind: e.kind,
      }));
    }
  }
  if (preview?.previewBranches?.length) {
    return preview.previewBranches.map((b) => ({
      fromStep: b.fromStep,
      label: b.label,
      toStep: b.toStep,
      fallback: String(b.label || "").toLowerCase() === "default",
    }));
  }
  const { branchingRows } = normalizePlanForDisplay(plan);
  return branchingRows.map((b) => ({
    fromStep: b.fromStep,
    label: b.label,
    toStep: b.toStep,
    fallback: b.fallback,
  }));
}

/** Variables for modal: merge preview.previewVariables with plan.variablePlan */
export function getPreviewVariableRows(preview, plan) {
  const rows = [];
  if (preview?.previewVariables?.length) {
    for (const v of preview.previewVariables) {
      rows.push({
        name: v.name,
        createdAtStep: v.createdAtStep,
        usedAtSteps: v.usedAtSteps,
      });
    }
  }
  if (plan?.variablePlan?.length && !rows.length) {
    for (const v of plan.variablePlan) {
      rows.push({
        name: v.name,
        createdAtStep: v.createdByStep,
        usedAtSteps: v.usedInSteps,
      });
    }
  }
  if (!rows.length && plan?.variables?.length) {
    for (const v of plan.variables) {
      rows.push({
        name: v.name,
        producedBy: v.producedBy,
        description: v.description,
      });
    }
  }
  return rows;
}

export function getPreviewStats(preview, plan, normalized) {
  const edgeCount = getPlanEdgeCount(plan, preview);
  const totalPlannedNodes =
    preview?.totalPlannedNodes ??
    plan?.plannedNodes?.length ??
    plan?.totalNodes ??
    normalized.steps.length ??
    0;

  let totalPlannedConnections =
    preview?.totalPlannedConnections ?? plan?.totalConnections ?? null;
  if (totalPlannedConnections == null) {
    if (edgeCount > 0) totalPlannedConnections = edgeCount;
    else if (normalized.connections.length > 0) {
      totalPlannedConnections = normalized.connections.length;
    } else if (normalized.branchingRows.length > 0) {
      totalPlannedConnections = normalized.branchingRows.length;
    } else {
      totalPlannedConnections = 0;
    }
  }

  return { totalPlannedNodes, totalPlannedConnections };
}
